import { useState } from 'react'
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material'
import { ContentCopy } from '@mui/icons-material'
import { Theme, useTheme } from '@mui/material/styles'
import { usePublisherInfo } from '../hooks/usePublisherInfo'
import { RSSUrlBuilder } from '../functions/RSSUrlBuilder'

const styles = {
  container: {
    p: 3
  },
  cardHeader: {
    backgroundColor: 'rgba(0, 188, 212, 0.1)',
    '& .MuiCardHeader-title': {
      fontSize: '0.875rem',
      color: '#00bcd4'
    }
  },
  codeBlockWrapper: {
    position: 'relative'
  },
  textField: (theme: Theme) => ({
    fontFamily: 'monospace',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.02)',
    paddingRight: '56px'
  }),
  copyButton: {
    position: 'absolute',
    right: 8,
    bottom: 8,
    backgroundColor: '#00bcd4',
    '&:hover': {
      backgroundColor: '#00a4b9'
    }
  },
  rssContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  alert: {
    width: '100%'
  }
}

export const AffiliateResources = () => {
  const theme = useTheme()
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const { publisher, status } = usePublisherInfo()
  const [rssUrl, atomUrl] = RSSUrlBuilder(publisher)

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setOpenSnackbar(true)
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  const CodeBlock = ({ text }: { text: string }) => (
    <Box sx={styles.codeBlockWrapper}>
      {status === 'loading' ? (
        <CircularProgress />
      ) : (
        <TextField
          multiline
          fullWidth
          value={text}
          variant="outlined"
          size="small"
          InputProps={{
            readOnly: true,
            sx: styles.textField(theme)
          }}
        />
      )}
      <Button
        variant="contained"
        onClick={() => handleCopy(text)}
        sx={styles.copyButton}
      >
        <ContentCopy />
      </Button>
    </Box>
  )

  return (
    <Grid container spacing={3} sx={styles.container}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardHeader title="AFFILIATE LINK" sx={styles.cardHeader} />
          <CardContent>
            <Typography variant="body2" component="p">
              This is your unique affiliate URL that will always link to the{' '}
              <strong>featured sale</strong>. Share it with your audience, and
              earn 10% of every purchase made using your link.
            </Typography>
            <CodeBlock
              text={`https://stacksocial.com?aid=${publisher?.code}`}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardHeader title="CUSTOM RSS FEED" sx={styles.cardHeader} />
          <CardContent>
            <Typography variant="body2" component="p">
              This custom RSS feed has your affiliate code included in all
              links. You can use it to get access to all of our active sales, as
              well as for notification of new sales.
            </Typography>
            <CodeBlock
              text={`https://stacksocial.com/feed?aid=${publisher?.code}`}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardHeader title="DEAL OF THE DAY TAB" sx={styles.cardHeader} />
          <CardContent>
            <CodeBlock
              text={`<script type="text/javascript" src="https://stacksocial-production-stacksocial.netdna-ssl.com/assets/dealOfTheDay.js"></script>
<script type="text/javascript">ss_DealOfTheDay.addUrlParam("aid=${publisher?.code}");</script>`}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardHeader
            title="UNIVERSAL AD UNIT (300X250)"
            sx={styles.cardHeader}
          />
          <CardContent>
            <CodeBlock
              text={`<iframe src="https://stacksocial.com/300250?aid=${publisher?.code}" width="300" height="250" style="border: none;"></iframe>`}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardHeader title="ARTICLE RSS FEED" sx={styles.cardHeader} />
          <CardContent>
            <Typography variant="body2" component="p">
              All articles written for you in a simple RSS feed format. Please
              find your RSS Feed URLs below. These URLs are specific to you.
            </Typography>
            <Box sx={styles.rssContainer}>
              <CodeBlock text={rssUrl} />
              <CodeBlock text={atomUrl} />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          sx={styles.alert}
        >
          Copied to clipboard!
        </Alert>
      </Snackbar>
    </Grid>
  )
}
